import React, { useState, useCallback, useImperativeHandle } from "react";
import { useSelector } from "react-redux";
import { Button } from "@euroland/base-ui";
import Label from "./ui/Label";
import { PickerDate } from "@euroland/calendar-react";

import CurrenciesSelect from "./CurrenciesSelect";
import { SHORT_DATE_FORMAT, TAB, TOOL_TEMPLATE } from "../constant/common";
import { useCurrencyEnabled, useFormatSetting, useTemplateSetting } from "../hooks/useCompanySettings";
import { classNames, findLatestDate } from "../utils";
import { i18n } from "@euroland/libs";
import { minStartingDateInstrumentSelector } from "../store/slice/trade";
import { renderTextBasedOnTemplate } from "../store/utils";
import useInstruments from "../hooks/useInstruments";
import { useMainContext } from "../contexts/MainContext";

const HistoricalSelection = ({ onSubmit, loading }, ref) => {
  const template = useTemplateSetting();
  const [currency, setCurrency] = useState("");
  const currencyEnabled = useCurrencyEnabled();
  const [{ instruments }] = useInstruments();
  const [pickerDate, setPickerDate] = useState({
    from: findLatestDate(instruments).toDate(),
    to: null,
  });

  const { tab } = useMainContext();
  const { currentTab } = tab;
  const isHistorycalTab = currentTab === TAB.history;

  const selectedCurrency = currency || i18n.translate("localCurrency");

  const isQuadrata = template === TOOL_TEMPLATE.quadrata;
  const isVictor = template === TOOL_TEMPLATE.victor;
  const minStartingDateInstrument = useSelector(minStartingDateInstrumentSelector);

  const handleChangeRangePicker = useCallback(data => {
    setPickerDate({
      from: data,
      to: null,
    });
  }, []);

  const handleChangeCurrencies = useCallback(val => setCurrency(val), []);

  const handleShowData = () => {
    onSubmit?.({
      currency: currency === i18n.translate("localCurrency") ? "" : currency,
      pickerDate,
    });
  };

  useImperativeHandle(ref, () => ({
    handleShowDataRef() {
      handleShowData();
    },
  }));

  const format = useFormatSetting();
  const calendarFormat = format.calendarDate || SHORT_DATE_FORMAT;
  const isRtl = window.appSettings.isRtl;

  // minDate and maxDate of date picker
  const minDate = minStartingDateInstrument ? new Date(minStartingDateInstrument) : null;
  const maxDate = findLatestDate(instruments).toDate();

  return (
    <>
      <div
        className={classNames(
          "historical-selection historical-selection-victor historical-selection-historicaltab",
          {
            "historical-selection-quadrata": isQuadrata,
            victor: isVictor,
            "hidden-currency": !currencyEnabled,
          }
        )}
      >
        <div className="historical-selection__wrapper">
          <div className={classNames("historical-selection__inputs")}>
            {isHistorycalTab && (
              <>
                <div className="select-period">
                  <Label htmlFor="startDate">
                    {renderTextBasedOnTemplate(
                      i18n.translate("labelDateVictorHistorical"),
                      i18n.translate("labelDateVictorHistorical")
                    )}
                  </Label>
                  <PickerDate
                    className={classNames({
                      left: (!isRtl && isVictor) || (isRtl && isQuadrata),
                      right: (!isRtl && isQuadrata) || (isRtl && isVictor),
                    })}
                    value={pickerDate?.from}
                    onChange={handleChangeRangePicker}
                    minDate={minDate}
                    maxDate={maxDate}
                    dateFormat={calendarFormat}
                    textCancel={i18n.translate("cancel")}
                    textApply={i18n.translate("apply")}
                    id="startDate"
                  />
                </div>
                <CurrenciesSelect onChange={handleChangeCurrencies} selectedCurrency={selectedCurrency} />
              </>
            )}
          </div>
          {/* {isVictor && <p className="historical-selection__note ">{noteDateTable}</p>} */}
          <Button
            className="historical-selection__submit historical-selection__submit-quadrata historical-tab-pc"
            onClick={handleShowData}
            outline={isQuadrata}
            active={isVictor}
            loading={loading}
          >
            {!isQuadrata && i18n.translate("showDataButton")}
            {isQuadrata && <span className="button-text">{i18n.translate("showDataButton")}</span>}
            {isQuadrata && <span className="fs-icon fs-right-dash-arrow"></span>}
          </Button>
        </div>

        <Button
          className="historical-selection__submit historical-selection__submit-quadrata historical-tab-mobile"
          onClick={handleShowData}
          outline={isQuadrata}
          active={isVictor}
          loading={loading}
        >
          {!isQuadrata && i18n.translate("showDataButton")}
          {isQuadrata && <span className="button-text">{i18n.translate("showDataButton")}</span>}
          {isQuadrata && <span className="fs-icon fs-right-dash-arrow"></span>}
        </Button>

        {/* {isVictor && <p className="historical-selection__note pc">{noteDateTable}</p>} */}
      </div>
      <div
          style={{ position: "absolute", width: 0, height: 0, overflow: "hidden", left: 0, bottom: 0 }}
          role="status"
          aria-live="polite"
        >
          {i18n.translate(loading ? "loading" : "loadingComplete")}
        </div>
    </>
  );
};

export default React.forwardRef(HistoricalSelection);
